/**
 * OS and Language aware code blocks with robust initialization
 */

// Get stored OS preference or detect from user agent
export function getSelectedOS(): 'macos' | 'windows' | 'linux' {
	if (typeof window === 'undefined') return 'linux';
	
	const stored = localStorage.getItem('heyweek-selected-os');
	if (stored && ['macos', 'windows', 'linux'].includes(stored)) {
		return stored as 'macos' | 'windows' | 'linux';
	}
	
	const platform = window.navigator.platform?.toLowerCase() || '';
	const userAgent = window.navigator.userAgent?.toLowerCase() || '';
	
	if (platform.includes('mac') || userAgent.includes('mac')) {
		return 'macos';
	} else if (platform.includes('win') || userAgent.includes('win')) {
		return 'windows';
	} else {
		return 'linux';
	}
}

// Get stored language preference or default to bash
export function getSelectedLanguage(): 'bash' | 'python' | 'nodejs' {
	if (typeof window === 'undefined') return 'bash';
	
	const stored = localStorage.getItem('heyweek-selected-language');
	if (stored && ['bash', 'python', 'nodejs'].includes(stored)) {
		return stored as 'bash' | 'python' | 'nodejs';
	}
	return 'bash';
}

// Apply the correct OS/language selection to all code blocks
export function applyCodeBlockSelection(os?: string, language?: string) {
	const selectedOS = os || getSelectedOS();
	const selectedLang = language || getSelectedLanguage();
	
	// Handle legacy OS code blocks
	document.querySelectorAll('.os-code-block').forEach(block => {
		block.querySelectorAll('.os-code-content').forEach((content: Element) => {
			const htmlContent = content as HTMLElement;
			const contentOS = htmlContent.dataset.os;
			htmlContent.style.display = contentOS === selectedOS ? 'block' : 'none';
		});
		
		// Update active OS tabs
		block.querySelectorAll('.os-code-tab').forEach((tab: Element) => {
			const tabElement = tab as HTMLElement;
			if (tabElement.dataset.os === selectedOS) {
				tabElement.classList.add('os-code-tab--active');
			} else {
				tabElement.classList.remove('os-code-tab--active');
			}
		});
	});
	
	// Handle enhanced code blocks
	document.querySelectorAll('.enhanced-code-block').forEach(block => {
		// Update content visibility
		block.querySelectorAll('.enhanced-code-content').forEach((content: Element) => {
			const htmlContent = content as HTMLElement;
			const contentOS = htmlContent.dataset.os;
			const contentLang = htmlContent.dataset.lang;
			const shouldShow = contentOS === selectedOS && contentLang === selectedLang;
			htmlContent.style.display = shouldShow ? 'block' : 'none';
		});
		
		// Update language tab active state
		block.querySelectorAll('.enhanced-code-lang-tab').forEach((tab: Element) => {
			const tabElement = tab as HTMLElement;
			if (tabElement.dataset.lang === selectedLang) {
				tabElement.classList.add('enhanced-code-lang-tab--active');
			} else {
				tabElement.classList.remove('enhanced-code-lang-tab--active');
			}
		});
	});
	
	// Save preferences
	if (os) localStorage.setItem('heyweek-selected-os', selectedOS);
	if (language) localStorage.setItem('heyweek-selected-language', selectedLang);
}

// Copy code from legacy OS blocks
function copyOSCode(blockId: string) {
	const block = document.querySelector(`[data-block-id="${blockId}"]`);
	if (!block) return;
	
	const activeContent = block.querySelector('.os-code-content:not([style*="display: none"])');
	if (!activeContent) return;
	
	const codeElement = activeContent.querySelector('code');
	if (!codeElement) return;
	
	const code = codeElement.textContent || '';
	navigator.clipboard.writeText(code).then(() => {
		const copyBtn = block.querySelector('.os-code-copy');
		const copyText = copyBtn?.querySelector('.copy-text');
		if (copyText) {
			copyText.textContent = 'Copied!';
			setTimeout(() => {
				copyText.textContent = 'Copy';
			}, 2000);
		}
	});
}

// Copy code from enhanced blocks
function copyEnhancedCode(blockId: string) {
	const block = document.querySelector(`[data-block-id="${blockId}"]`);
	if (!block) return;
	
	const activeContent = block.querySelector('.enhanced-code-content:not([style*="display: none"])');
	if (!activeContent) return;
	
	const codeElement = activeContent.querySelector('code');
	if (!codeElement) return;
	
	// Extract text content, skipping line numbers
	const lines = codeElement.querySelectorAll('.code-line');
	let code = '';
	
	if (lines.length > 0) {
		code = Array.from(lines).map(line => {
			const content = line.querySelector('.line-content');
			return content?.textContent || '';
		}).join('\n');
	} else {
		// Fallback for code blocks without line numbers
		code = codeElement.textContent || '';
	}
	
	navigator.clipboard.writeText(code).then(() => {
		const copyBtn = block.querySelector('.enhanced-code-copy');
		const copyText = copyBtn?.querySelector('.copy-text');
		if (copyText) {
			copyText.textContent = 'Copied!';
			setTimeout(() => {
				copyText.textContent = 'Copy';
			}, 2000);
		}
	});
}

// Initialize event handlers for a single code block
function initializeCodeBlock(block: Element) {
	const blockId = block.getAttribute('data-block-id');
	if (!blockId) return;
	
	// Remove any existing listeners first (prevents duplicates)
	const newBlock = block.cloneNode(true) as Element;
	block.parentNode?.replaceChild(newBlock, block);
	
	if (newBlock.classList.contains('os-code-block')) {
		// Legacy OS block
		const copyBtn = newBlock.querySelector('.os-code-copy');
		if (copyBtn) {
			copyBtn.addEventListener('click', () => copyOSCode(blockId));
		}
		
		// OS tab clicks
		newBlock.querySelectorAll('.os-code-tab').forEach(tab => {
			tab.addEventListener('click', () => {
				const tabElement = tab as HTMLElement;
				const os = tabElement.dataset.os;
				if (os) {
					applyCodeBlockSelection(os, undefined);
					window.dispatchEvent(new CustomEvent('os-selection-changed', { detail: { os } }));
				}
			});
		});
	} else if (newBlock.classList.contains('enhanced-code-block')) {
		// Enhanced block
		const copyBtn = newBlock.querySelector('.enhanced-code-copy');
		if (copyBtn) {
			copyBtn.addEventListener('click', () => copyEnhancedCode(blockId));
		}
		
		// Language tab clicks
		newBlock.querySelectorAll('.enhanced-code-lang-tab').forEach(tab => {
			tab.addEventListener('click', () => {
				const tabElement = tab as HTMLElement;
				const language = tabElement.dataset.lang;
				if (language) {
					applyCodeBlockSelection(undefined, language);
					window.dispatchEvent(new CustomEvent('language-selection-changed', { detail: { language } }));
				}
			});
		});
	}
}

// Main initialization function
export function initializeEnhancedCodeBlocks() {
	// Apply current selection immediately
	applyCodeBlockSelection();
	
	// Initialize all code blocks
	document.querySelectorAll('.os-code-block, .enhanced-code-block').forEach(block => {
		initializeCodeBlock(block);
	});
	
	// Make functions globally available for inline onclick handlers
	if (typeof window !== 'undefined') {
		(window as any).copyOSCode = copyOSCode;
		(window as any).copyEnhancedCode = copyEnhancedCode;
		(window as any).switchOSContent = (os: string) => {
			applyCodeBlockSelection(os, undefined);
			window.dispatchEvent(new CustomEvent('os-selection-changed', { detail: { os } }));
		};
		(window as any).switchLanguageContent = (language: string) => {
			applyCodeBlockSelection(undefined, language);
			window.dispatchEvent(new CustomEvent('language-selection-changed', { detail: { language } }));
		};
	}
	
	// Listen for cross-tab storage changes
	window.addEventListener('storage', (e) => {
		if (e.key === 'heyweek-selected-os' || e.key === 'heyweek-selected-language') {
			applyCodeBlockSelection();
		}
	});
	
	// Listen for custom events
	window.addEventListener('os-selection-changed', (e: Event) => {
		const customEvent = e as CustomEvent;
		if (customEvent.detail?.os) {
			applyCodeBlockSelection(customEvent.detail.os, undefined);
		}
	});
	
	window.addEventListener('language-selection-changed', (e: Event) => {
		const customEvent = e as CustomEvent;
		if (customEvent.detail?.language) {
			applyCodeBlockSelection(undefined, customEvent.detail.language);
		}
	});
}

// Export a function to apply immediate styles (for SSR)
export function getImmediateStyles(): string {
	const os = getSelectedOS();
	const lang = getSelectedLanguage();
	
	return `
		/* Legacy OS blocks */
		.os-code-content[data-os="${os}"] { display: block !important; }
		.os-code-content:not([data-os="${os}"]) { display: none !important; }
		
		/* Enhanced code blocks */
		.enhanced-code-content[data-os="${os}"][data-lang="${lang}"] { display: block !important; }
		.enhanced-code-content:not([data-os="${os}"]) { display: none !important; }
		.enhanced-code-content[data-os="${os}"]:not([data-lang="${lang}"]) { display: none !important; }
		
		/* Active tab styling */
		.enhanced-code-lang-tab[data-lang="${lang}"] { 
			background: var(--bg-primary) !important; 
			color: var(--text-primary) !important; 
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important; 
		}
	`;
}

// Legacy export for backward compatibility
export const initializeOSCodeBlocks = initializeEnhancedCodeBlocks;